import React, { useEffect, useState } from "react";

/**
 * Live countdown timer. Shows DD:HH:MM:SS or HH:MM:SS depending on
 * how far away the target is. Self-cleans on unmount.
 *
 * Props:
 *   targetIso  ISO datetime string in the future (e.g. product.sale_ends_at)
 *   onExpire?  optional callback fired when timer hits zero
 *   compact?   bool — render the smaller PDP-friendly layout
 */
export default function CountdownTimer({ targetIso, onExpire, compact = false }) {
  const target = targetIso ? new Date(targetIso).getTime() : null;

  const computeRemaining = () => {
    if (!target) return null;
    const ms = target - Date.now();
    if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    return {
      days: Math.floor(ms / 86400000),
      hours: Math.floor((ms % 86400000) / 3600000),
      minutes: Math.floor((ms % 3600000) / 60000),
      seconds: Math.floor((ms % 60000) / 1000),
      expired: false,
    };
  };

  const [remaining, setRemaining] = useState(computeRemaining);

  useEffect(() => {
    if (!target) return undefined;
    const tick = () => {
      const r = computeRemaining();
      setRemaining(r);
      if (r?.expired && typeof onExpire === "function") onExpire();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso]);

  if (!target || !remaining) return null;
  if (remaining.expired) return null;

  const cellCls = compact
    ? "flex flex-col items-center justify-center px-2 py-1.5 rounded-sm border border-[#A6FF00]/40 bg-[#0B1220]/80 min-w-[42px]"
    : "flex flex-col items-center justify-center px-3 py-2.5 rounded-sm border-2 border-[#A6FF00]/50 bg-[#0B1220]/90 min-w-[64px] sm:min-w-[72px] backdrop-blur-sm";
  const numCls = compact
    ? "pg-display text-[#A6FF00] font-extrabold text-base leading-none tabular-nums"
    : "pg-display text-[#A6FF00] font-extrabold text-2xl sm:text-3xl leading-none tabular-nums";
  const lblCls = "pg-mono text-[8px] sm:text-[9px] uppercase tracking-[0.16em] text-[#9CA8B8] mt-1";

  const showDays = remaining.days > 0;
  const sep = (
    <span
      className={
        compact
          ? "pg-display text-[#A6FF00]/70 text-base font-extrabold leading-none"
          : "pg-display text-[#A6FF00]/70 text-2xl sm:text-3xl font-extrabold leading-none px-0.5"
      }
    >
      :
    </span>
  );

  return (
    <div
      className="inline-flex items-center gap-1.5"
      data-testid="countdown-timer"
      style={{ fontFamily: "'Exo 2', sans-serif" }}
    >
      {showDays && (
        <>
          <div className={cellCls}>
            <span className={numCls}>{String(remaining.days).padStart(2, "0")}</span>
            <span className={lblCls}>Days</span>
          </div>
          {sep}
        </>
      )}
      <div className={cellCls}>
        <span className={numCls}>{String(remaining.hours).padStart(2, "0")}</span>
        <span className={lblCls}>Hrs</span>
      </div>
      {sep}
      <div className={cellCls}>
        <span className={numCls}>{String(remaining.minutes).padStart(2, "0")}</span>
        <span className={lblCls}>Min</span>
      </div>
      {sep}
      <div className={cellCls}>
        <span className={numCls}>{String(remaining.seconds).padStart(2, "0")}</span>
        <span className={lblCls}>Sec</span>
      </div>
    </div>
  );
}
